import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import LiveSessionFormDialog, { type LiveSessionFormValues } from '@/components/LiveSessionFormDialog';
import LiveSessionCard from '@/components/live/LiveSessionCard';
import type { LiveSession } from '@/types';

export default function LiveListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = () => {
    setLoading(true);
    api
      .get<{ sessions: LiveSession[] }>('/live-sessions', { params: { q } })
      .then((res) => setSessions(res.data.sessions))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const tick = () => {
      api
        .get<{ sessions: LiveSession[] }>('/live-sessions', { params: { q } })
        .then((res) => setSessions(res.data.sessions))
        .catch(() => {});
    };
    const interval = window.setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, [q]);

  const handleCreate = async (values: LiveSessionFormValues) => {
    setCreateError(null);
    try {
      const res = await api.post<{ session: LiveSession }>('/live-sessions', values);
      setDialogOpen(false);
      navigate(`/live/${res.data.session.id}`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '無法建立即時記錄';
      setCreateError(msg);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('確定刪除此即時場次？')) return;
    await api.delete(`/live-sessions/${id}`);
    load();
  };

  return (
    <Box>
      <PageHeader
        icon={<SportsHandballIcon />}
        title="即時記錄"
        subtitle="現場快速標記裁判判決，賽後再作詳細分析"
        chips={
          !loading ? (
            <Chip size="small" color="secondary" variant="outlined" label={`${sessions.length} 場`} />
          ) : undefined
        }
        actions={
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            新增
          </Button>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="搜尋賽事、隊伍"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
          fullWidth
          sx={{ maxWidth: 360 }}
        />
        <Button variant="outlined" onClick={load}>
          篩選
        </Button>
      </Stack>

      {loading ? (
        <Typography>載入中…</Typography>
      ) : sessions.length === 0 ? (
        <Typography color="text.secondary">尚無即時場次 — 按「新增」建立第一場比賽記錄</Typography>
      ) : (
        sessions.map((s) => <LiveSessionCard key={s.id} session={s} onDelete={handleDelete} />)
      )}

      <LiveSessionFormDialog
        open={dialogOpen}
        title="新增即時記錄"
        error={createError}
        onClose={() => {
          setDialogOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreate}
      />
    </Box>
  );
}
